import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LoadTargetAdjustment, LoadTargetColumn } from '@/modules/supply/components/SupplyLoadVisualization';
import {
  supplyAgentService,
  type SupplyAgentFillObjective,
  type SupplyAgentLogisticsCalculateResponse,
  type SupplyAgentOverCapacityOption,
  type SupplyAgentShipmentMode,
} from '@/services/supplyAgentService';

export type FillStrategyCode = 'BALANCE_COVERAGE' | 'ROTATION_LAYERS' | 'MARGIN_PRIORITY' | 'HIGH_ROTATION_ONLY';

/** Estrategias de «Completar hasta objetivo»; la primera es la de la spec por defecto. */
export const FILL_STRATEGY_OPTIONS: ReadonlyArray<{ code: FillStrategyCode; labelKey: string }> = [
  { code: 'BALANCE_COVERAGE', labelKey: 'supply.supplyAgent.fillToTargetStrategyBalance' },
  { code: 'ROTATION_LAYERS', labelKey: 'supply.supplyAgent.fillToTargetStrategyLayers' },
  { code: 'MARGIN_PRIORITY', labelKey: 'supply.supplyAgent.fillToTargetStrategyMargin' },
  { code: 'HIGH_ROTATION_ONLY', labelKey: 'supply.supplyAgent.fillToTargetStrategyHighRotation' },
];

/** Cantidad final de una línea tras aplicar la propuesta (0 = el producto sale del pedido). */
export interface FillQuantityChange {
  supplierId: number;
  productId: number;
  units: number;
}

interface AppliedChange {
  supplierId: number;
  productId: number;
  originalUnits: number;
  newUnits: number;
  coverageBefore: number | null;
  coverageAfter: number | null;
}

interface Params {
  warehouseId: number | null;
  locale: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Proveedores que comparten el vehículo que se está viendo. */
  supplierIds: number[];
  /** Proveedor cuyo contenedor se usa de referencia. */
  referenceSupplierId: number | null;
  /** Contenedor elegido para el vehículo (si no, el de la regla del proveedor). */
  containerTemplateId: number | null;
  /** Cantidades vigentes {producto: unidades} de los proveedores del vehículo. */
  buildQuantities: () => Record<string, number>;
  lockedProductIds: number[];
  /** Aplica cantidades a las estimaciones (recalcula los proveedores afectados). */
  applyQuantities: (changes: FillQuantityChange[]) => Promise<void>;
  /** Cambia cuando se pasa a ver otro vehículo: la propuesta y lo aplicado dejan de valer. */
  resetKey: string;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/** «Aplicar restricción logística» (completar hasta un objetivo de carga) del módulo de estimación. */
export function useLoadRestriction({
  warehouseId, locale, t, supplierIds, referenceSupplierId, containerTemplateId, buildQuantities, lockedProductIds, applyQuantities, resetKey,
}: Params) {
  const [open, setOpen] = useState(false);
  const [objective, setObjective] = useState<SupplyAgentFillObjective>('container');
  const [targetAmount, setTargetAmount] = useState('');
  const [saving, setSaving] = useState('');
  const [targetPallets, setTargetPallets] = useState('');
  const [strategy, setStrategy] = useState<FillStrategyCode>('BALANCE_COVERAGE');
  const [expandAssortment, setExpandAssortment] = useState(false);
  // Tope de cobertura total (días) de esta simulación; vacío = el de la regla del proveedor.
  const [maxCoverageDays, setMaxCoverageDays] = useState('');
  // Tope de caducidad: ningún producto pasa de lo que se vende en su vida útil cargada.
  const [respectShelfLife, setRespectShelfLife] = useState(false);
  // Envío completo (contenedor entero) o parcial (LCL): en parcial la capacidad es el volumen/peso contratado.
  const [shipmentMode, setShipmentMode] = useState<SupplyAgentShipmentMode>('fcl');
  const [contractedVolume, setContractedVolume] = useState('');
  const [contractedWeight, setContractedWeight] = useState('');
  const [overOption, setOverOption] = useState<SupplyAgentOverCapacityOption>('reduce');
  const [proposal, setProposal] = useState<SupplyAgentLogisticsCalculateResponse | null>(null);
  const [accepted, setAccepted] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<Record<string, AppliedChange>>({});

  useEffect(() => {
    setProposal(null);
    setApplied({});
    setAccepted({});
    setError(null);
  }, [resetKey]);

  const request = useCallback(async (optionOverride?: SupplyAgentOverCapacityOption) => {
    if (supplierIds.length < 1 || referenceSupplierId == null || typeof warehouseId !== 'number') return;
    const option = optionOverride ?? overOption;
    setOverOption(option);
    setLoading(true);
    setError(null);
    // Al cambiar de opción se mantiene la propuesta visible mientras llega la nueva.
    if (!optionOverride) setProposal(null);
    try {
      const result = await supplyAgentService.logisticsCalculate({
        mode: 'SIMULATE',
        supplier_id: referenceSupplierId,
        warehouse_id: warehouseId,
        load_suggested_order: true,
        strategy: { code: strategy },
        ...(supplierIds.length > 1 ? { supplier_ids: supplierIds } : {}),
        ...(containerTemplateId != null ? { container_template_id: containerTemplateId } : {}),
        quantity_overrides: buildQuantities(),
        expand_assortment: expandAssortment,
        ...(Number(maxCoverageDays) > 0 ? { max_total_coverage_days: Number(maxCoverageDays) } : {}),
        ...(respectShelfLife ? { respect_shelf_life: true } : {}),
        over_capacity_option: option,
        locked_product_ids: lockedProductIds,
        ...(shipmentMode === 'lcl'
          ? {
            shipment_mode: 'lcl' as const,
            ...(Number(contractedVolume) > 0 ? { contracted_volume_m3: Number(contractedVolume) } : {}),
            ...(Number(contractedWeight) > 0 ? { contracted_weight_kg: Number(contractedWeight) } : {}),
          }
          : {}),
        ...(objective === 'amount'
          ? {
            objective: 'amount' as const,
            ...(Number(targetAmount) > 0 ? { target_amount_euros: Number(targetAmount) } : {}),
            ...(Number(saving) > 0 ? { saving_euros: Number(saving) } : {}),
          }
          : objective === 'pallet'
            ? { objective: 'pallet' as const, ...(Number(targetPallets) > 0 ? { target_pallets: Number(targetPallets) } : {}) }
            : objective === 'layer'
              ? { objective: 'layer' as const }
              : {}),
        locale,
      });
      setProposal(result);
      setAccepted(Object.fromEntries((result.line_adjustments || []).map((adjustment) => [adjustment.product_id, true])));
    } catch (e) {
      setError(apiErrorMessage(e, t('supply.supplyAgent.fillToTargetError')));
    } finally {
      setLoading(false);
    }
  }, [supplierIds, referenceSupplierId, warehouseId, overOption, strategy, containerTemplateId, buildQuantities, expandAssortment, maxCoverageDays, respectShelfLife, lockedProductIds, shipmentMode, contractedVolume, contractedWeight, objective, targetAmount, saving, targetPallets, locale, t]);

  const apply = useCallback(async () => {
    if (!proposal || referenceSupplierId == null) return;
    const chosen = (proposal.line_adjustments || []).filter((adjustment) => accepted[adjustment.product_id]);
    if (chosen.length === 0) return;
    const supplierOf = (adjustment: (typeof chosen)[number]) => adjustment.supplier_id ?? referenceSupplierId;
    setLoading(true);
    setError(null);
    try {
      await applyQuantities(chosen.map((adjustment) => ({
        supplierId: supplierOf(adjustment),
        productId: adjustment.product_id,
        units: Number(adjustment.new_units) || 0,
      })));
      setApplied((previous) => {
        const next = { ...previous };
        chosen.forEach((adjustment) => {
          const key = `${supplierOf(adjustment)}:${adjustment.product_id}`;
          const earlier = next[key];
          // Si ya se había aplicado un ajuste, conserva las unidades originales.
          next[key] = {
            supplierId: supplierOf(adjustment),
            productId: adjustment.product_id,
            originalUnits: earlier ? earlier.originalUnits : Number(adjustment.old_units) || 0,
            newUnits: Number(adjustment.new_units) || 0,
            coverageBefore: earlier ? earlier.coverageBefore : adjustment.coverage_days_before ?? null,
            coverageAfter: adjustment.coverage_days_after ?? null,
          };
        });
        return next;
      });
      setProposal(null);
    } catch (e) {
      setError(apiErrorMessage(e, t('supply.supplyAgent.fillToTargetError')));
    } finally {
      setLoading(false);
    }
  }, [proposal, referenceSupplierId, accepted, applyQuantities, t]);

  const undo = useCallback(async () => {
    const changes = Object.values(applied).map((change) => ({ supplierId: change.supplierId, productId: change.productId, units: change.originalUnits }));
    if (changes.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      await applyQuantities(changes);
      setApplied({});
    } catch (e) {
      setError(apiErrorMessage(e, t('supply.supplyAgent.fillToTargetError')));
    } finally {
      setLoading(false);
    }
  }, [applied, applyQuantities, t]);

  // Columna «Ajuste objetivo» de la tabla de productos: propuesta pendiente o ajuste aplicado.
  const targetColumn = useMemo<LoadTargetColumn | null>(() => {
    if (proposal) {
      const byProduct: Record<number, LoadTargetAdjustment> = {};
      (proposal.line_adjustments || []).forEach((adjustment) => {
        const lineSupplierId = adjustment.supplier_id ?? referenceSupplierId;
        const appliedBefore = lineSupplierId != null ? applied[`${lineSupplierId}:${adjustment.product_id}`] : undefined;
        byProduct[adjustment.product_id] = {
          originalUnits: appliedBefore ? appliedBefore.originalUnits : Number(adjustment.old_units) || 0,
          newUnits: Number(adjustment.new_units) || 0,
          coverageBefore: adjustment.coverage_days_before ?? null,
          coverageAfter: adjustment.coverage_days_after ?? null,
          label: adjustment.product_name || undefined,
          sku: adjustment.sku || undefined,
        };
      });
      return {
        state: 'proposal',
        byProduct,
        accepted,
        onToggle: (productId, isAccepted) => setAccepted((current) => ({ ...current, [productId]: isAccepted })),
      };
    }
    const changes = Object.values(applied);
    if (changes.length === 0) return null;
    return {
      state: 'applied',
      byProduct: Object.fromEntries(changes.map((change) => [change.productId, {
        originalUnits: change.originalUnits,
        newUnits: change.newUnits,
        coverageBefore: change.coverageBefore,
        coverageAfter: change.coverageAfter,
      }])),
    };
  }, [proposal, accepted, applied, referenceSupplierId]);

  return {
    open, setOpen,
    objective, setObjective,
    targetAmount, setTargetAmount,
    saving, setSaving,
    targetPallets, setTargetPallets,
    strategy, setStrategy,
    expandAssortment, setExpandAssortment,
    maxCoverageDays, setMaxCoverageDays,
    respectShelfLife, setRespectShelfLife,
    shipmentMode, setShipmentMode,
    contractedVolume, setContractedVolume,
    contractedWeight, setContractedWeight,
    lclWithoutContract: shipmentMode === 'lcl' && !(Number(contractedVolume) > 0) && !(Number(contractedWeight) > 0),
    proposal, setProposal,
    accepted, setAccepted,
    loading, error,
    canUndo: Object.keys(applied).length > 0,
    targetColumn,
    request, apply, undo,
  };
}

export type LoadRestriction = ReturnType<typeof useLoadRestriction>;
